import useGetCanBulkExport from '@/api/queries/me/useGetCanBulkExport';
import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import { usePermissions } from '@/hooks/usePermissions';
import { useAccount } from '@/api/rest/users/useAccount';
import { useRegion } from '@/api/rest/users/useRegion';
import { cansupportTwilioSenderHandle } from '@payaca/helpers/internationalHelper';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { AppliancesPermissions } from '@payaca/permissions/appliances/appliances.permissions';
import { ChatAgentPermissions } from '@payaca/permissions/chatAgent';
import { DocumentTemplatePermissions } from '@payaca/permissions/documentTemplate/documentTemplate.permissions';
import { EstimatesPermissions } from '@payaca/permissions/estimates/estimates.permissions';
import { TemplatesPermissions } from '@payaca/permissions/templates/templates.permissions';
import { TimelogsPermissions } from '@payaca/permissions/timelogs/timelogs.permissions';
import { AccountRegions } from '@payaca/types/accountTypes';

export type SettingsCategoryLabel =
  | 'Company'
  | 'Team'
  | 'Sales & payments'
  | 'Communications'
  | 'Operations'
  | 'Custom data'
  | 'Integrations & data';

/**
 * Renders in the settings pane at `/companySettings/<categorySlug>/<slug>`.
 * `keywords` name the individual settings inside, so the nav search finds a
 * section by what it contains (e.g. "vat" -> Tax).
 */
export type SettingsSubSection = {
  label: string;
  slug: string;
  route: string;
  /**
   * The account-settings forms need account-editor; sections like Users or
   * Tags have their own weaker permission gates and stay visible without it.
   */
  requiresAccountEditor: boolean;
  keywords: Array<string>;
  /** Plain-language line under the page heading. Used for branded products. */
  description?: string;
};

export type SettingsCategory = {
  label: SettingsCategoryLabel;
  /** URL segment under /companySettings for internal categories. */
  slug: string;
  /** Where selecting the category itself navigates (its first sub-section). */
  route: string;
  subSections: Array<SettingsSubSection>;
};

const COMPANY_SETTINGS_BASE = '/companySettings';

/**
 * The Company Settings information architecture, permission-gated. Rendered on
 * the settings shell and on the external areas it links into.
 */
export const useSettingsNav = (): Array<SettingsCategory> => {
  const { userHasRequiredPermission } = usePermissions();
  const { canBulkExport } = useGetCanBulkExport();
  const account = useAccount();
  const region = useRegion();

  const isUk = [AccountRegions.UK].includes(region);
  // UPDATE_ACCOUNT maps to exactly AccountsRoles.EDITOR — the role the settings
  // pane and the blueprint library routes require.
  const canUpdateAccount = userHasRequiredPermission([
    AccountsPermissions.UPDATE_ACCOUNT,
  ]);
  const canShowMessaging = cansupportTwilioSenderHandle(region);
  const canViewTeam = userHasRequiredPermission([
    AccountsPermissions.GET_DETAILED_USERS,
  ]);
  const canManageChatAgent = userHasRequiredPermission([
    ChatAgentPermissions.MANAGE_INSTRUCTIONS,
  ]);
  const canManageDeveloper = userHasRequiredPermission([
    AccountsPermissions.MANAGE_API_KEYS,
    AccountsPermissions.MANAGE_WEBHOOKS,
  ]);
  const canViewTemplates = userHasRequiredPermission([
    TemplatesPermissions.GET_COLLECTION_MANAGEMENT_VIEW,
  ]);
  // Proposal and Document templates are separately gated pages; linking to one
  // the user cannot open silently redirects them to Event templates.
  const hasHeatDesignTool = useMyAccountHasFeature('hasHeatDesignTool');
  const hasSolarDesignTool = useMyAccountHasFeature('hasSolarDesignTool');
  const canManageEstimateSettings = userHasRequiredPermission([
    AppliancesPermissions.MANAGE_APPLIANCES,
    EstimatesPermissions.GET_DEFAULT_ITEMS,
  ]);
  const canManageFlow = hasHeatDesignTool && canManageEstimateSettings;
  const canManageGlow = hasSolarDesignTool && canManageEstimateSettings;
  const canViewProposalTemplates =
    canViewTemplates &&
    userHasRequiredPermission([TemplatesPermissions.USE_PROPOSAL_TEMPLATES]);
  const canViewDocumentTemplates =
    canViewTemplates &&
    userHasRequiredPermission([
      DocumentTemplatePermissions.GET_DOCUMENT_TEMPLATES,
    ]);
  const canManageCustomFields = userHasRequiredPermission([
    AccountsPermissions.MANAGE_CUSTOM_FIELD_GROUPS,
  ]);
  const canManageCustomFieldValues = userHasRequiredPermission([
    AccountsPermissions.MANAGE_CUSTOM_FIELD_VALUES,
  ]);
  const canManageTags = userHasRequiredPermission([
    AccountsPermissions.MANAGE_TAGS,
  ]);
  const canManageTimelogTypes = userHasRequiredPermission([
    TimelogsPermissions.MANAGE_TIMELOG_TYPES,
  ]);
  const canViewConnections = userHasRequiredPermission([
    AccountsPermissions.UPDATE_CONNECTION,
    AccountsPermissions.UPDATE_CONNECTION_ACCOUNTING,
    AccountsPermissions.UPDATE_CONNECTION_SUPPLIERS,
    AccountsPermissions.UPDATE_CONNECTION_PAYMENT,
    AccountsPermissions.UPDATE_CONNECTION_AUTOMATION,
  ]);

  const internalSub = (
    categorySlug: string,
    slug: string,
    label: string,
    keywords: Array<string>,
    description?: string
  ): SettingsSubSection => ({
    label,
    slug,
    route: `${COMPANY_SETTINGS_BASE}/${categorySlug}/${slug}`,
    requiresAccountEditor: true,
    keywords,
    description,
  });

  /** A section with its own permission gate, reachable without account-editor. */
  const sharedSub = (
    categorySlug: string,
    slug: string,
    label: string,
    keywords: Array<string>
  ): SettingsSubSection => ({
    label,
    slug,
    route: `${COMPANY_SETTINGS_BASE}/${categorySlug}/${slug}`,
    requiresAccountEditor: false,
    keywords,
  });

  const category = (
    label: SettingsCategoryLabel,
    slug: string,
    subSections: Array<SettingsSubSection>
  ): SettingsCategory => ({
    label,
    slug,
    route: subSections[0]?.route ?? `${COMPANY_SETTINGS_BASE}/${slug}`,
    subSections,
  });

  const categories: Array<SettingsCategory> = [];

  categories.push(
    category('Company', 'company', [
      internalSub('company', 'business-details', 'Business details', [
        'trading name',
        'company name',
        'email',
        'telephone',
        'phone',
        'address',
        'postcode',
        'registered country',
        'company registration number',
      ]),
      internalSub('company', 'brand-identity', 'Brand identity', [
        'logo',
        'brand colour',
        'brand color',
        'marketing material',
        'credentials',
        'client portal',
        'subdomain',
        'branding',
      ]),
      ...(isUk
        ? [
            internalSub('company', 'accreditations', 'Accreditations', [
              'gas safe',
              'niceic',
              'accreditation',
              'registration number',
            ]),
          ]
        : []),
      // Saving needs UPDATE_ACCOUNT as well, so show it to account editors only.
      ...(canManageCustomFieldValues && canUpdateAccount
        ? [
            internalSub('company', 'company-fields', 'Company fields', [
              'custom fields',
              'company fields',
              'certificate numbers',
              'accreditation',
            ]),
          ]
        : []),
      ...(canManageChatAgent
        ? [
            internalSub('company', 'agent-dave', 'Agent Dave', [
              'ai',
              'assistant',
              'instructions',
              'chat agent',
            ]),
          ]
        : []),
    ])
  );

  if (canViewTeam) {
    categories.push(
      category('Team', 'team', [
        sharedSub('team', 'users', 'Users & teams', [
          'users',
          'teams',
          'groups',
          'group',
          'staff',
          'invite',
          'role',
          'permissions',
          'deactivate',
          'cost per hour',
          'members',
        ]),
      ])
    );
  }

  categories.push(
    category('Sales & payments', 'sales-payments', [
      internalSub(
        'sales-payments',
        'proposals-invoices',
        'Proposals & invoices',
        [
          'quote',
          'quotes',
          'proposal valid days',
          'invoice due days',
          'payment terms',
          'deposit',
          'default deposit percentage',
          'hide item prices',
          'hide tax breakdown',
          'project defaults',
        ]
      ),
      ...(canViewProposalTemplates
        ? [
            sharedSub(
              'sales-payments',
              'proposal-templates',
              'Proposal templates',
              ['proposal template', 'quote template']
            ),
          ]
        : []),
      ...(canManageFlow
        ? [
            internalSub(
              'sales-payments',
              'flow',
              'Flow',
              [
                'appliances',
                'heat pump',
                'heat pumps',
                'cylinder',
                'cylinders',
                'default estimate items',
                'heating',
                'cooling',
                'estimates',
              ],
              'Heating & cooling'
            ),
          ]
        : []),
      ...(canManageGlow
        ? [
            internalSub(
              'sales-payments',
              'glow',
              'Glow',
              [
                'appliances',
                'solar',
                'solar panel',
                'solar panels',
                'inverter',
                'inverters',
                'battery',
                'batteries',
                'tariff',
                'tariffs',
                'export rate',
                'savings',
                'estimates',
              ],
              'Solar'
            ),
          ]
        : []),
      internalSub('sales-payments', 'terms', 'Terms', [
        'terms of business',
        'required terms',
        'terms and conditions',
      ]),
      internalSub('sales-payments', 'payments', 'Payments', [
        'stripe',
        'card payments',
        'bank transfer',
        'bank details',
        'sort code',
        'account number',
        'external payment redirect',
        'getting paid',
      ]),
      internalSub('sales-payments', 'tax', 'Tax', [
        'vat',
        'vat number',
        'sales tax',
        'tax rate',
        'tax rates',
        'cis',
        'construction industry scheme',
        'subcontractor',
        'deduction',
      ]),
    ])
  );

  categories.push(
    category('Communications', 'communications', [
      internalSub('communications', 'email', 'Email', [
        'sending address',
        'custom domain',
        'dkim',
        'return-path',
        'send from',
        'email template',
        'outbound',
      ]),
      ...(canShowMessaging
        ? [
            internalSub('communications', 'messaging', 'Messaging', [
              'sms',
              'text message',
              'sender handle',
            ]),
          ]
        : []),
    ])
  );

  categories.push(
    category('Operations', 'operations', [
      internalSub('operations', 'schedule', 'Schedule', [
        'business hours',
        'event reminders',
        'event colour',
        'event color',
        'scheduled event',
        'calendar',
      ]),
      internalSub('operations', 'primary-pipeline', 'Primary pipeline', [
        'primary pipeline',
        'pipeline',
        'pipelines',
        'default pipeline',
      ]),
      ...(canViewTemplates
        ? [
            sharedSub('operations', 'event-templates', 'Event templates', [
              'event template',
              'event templates',
            ]),
            sharedSub('operations', 'task-templates', 'Task templates', [
              'task template',
              'task templates',
            ]),
          ]
        : []),
      {
        ...internalSub('operations', 'field-service', 'Field service', [
          'suppliers',
          'field agent',
          'fieldsets',
          'time tracking',
          'timelog',
          'timelogs',
        ]),
        requiresAccountEditor: !canManageTimelogTypes,
      },
    ])
  );

  categories.push(
    category('Custom data', 'custom-data', [
      ...(canManageCustomFields
        ? [
            internalSub(
              'custom-data',
              'project-custom-fields',
              'Project custom fields',
              [
                'custom fields',
                'custom fieldsets',
                'project fields',
                'form',
                'forms',
              ]
            ),
            internalSub(
              'custom-data',
              'customer-custom-fields',
              'Customer custom fields',
              ['custom fields', 'custom fieldsets', 'customer fields']
            ),
            internalSub(
              'custom-data',
              'account-custom-fields',
              'Company custom fields',
              [
                'custom fields',
                'custom fieldsets',
                'company fields',
                'account fields',
              ]
            ),
          ]
        : []),
      ...(canManageTags
        ? [sharedSub('custom-data', 'tags', 'Tags', ['tags', 'labels'])]
        : []),
      ...(canViewDocumentTemplates
        ? [
            sharedSub(
              'custom-data',
              'document-templates',
              'Document templates',
              ['document template', 'form', 'certificate', 'certificates']
            ),
          ]
        : []),
      // The library requires account-editor, like the settings forms.
      ...(canUpdateAccount
        ? [
            internalSub(
              'custom-data',
              'ready-made-library',
              'Ready-made library',
              ['document blueprint', 'blueprints', 'ready-made', 'library']
            ),
          ]
        : []),
    ])
  );

  const integrationsSubSections: Array<SettingsSubSection> = [];
  if (canViewConnections) {
    integrationsSubSections.push(
      sharedSub('integrations-data', 'connections', 'Connections', [
        'xero',
        'quickbooks',
        'stripe',
        'zapier',
        'opensolar',
        'city plumbing',
        'partner apps',
        'integrations',
      ])
    );
  }
  if (canManageDeveloper) {
    integrationsSubSections.push(
      internalSub('integrations-data', 'developer', 'Developer', [
        'api keys',
        'api token',
        'api',
        'webhooks',
      ])
    );
  }
  if (canBulkExport) {
    integrationsSubSections.push(
      internalSub('integrations-data', 'data-export', 'Data export', [
        'export account data',
        'csv',
        'download data',
      ])
    );
  }
  if (integrationsSubSections.length > 0) {
    categories.push(
      category(
        'Integrations & data',
        'integrations-data',
        integrationsSubSections
      )
    );
  }

  // Without account-editor keep only the sections with their own weaker gates,
  // dropping any category left empty rather than showing a dead nav.
  if (canUpdateAccount) return categories;

  return categories
    .map((category) => {
      const subSections = category.subSections.filter(
        (sub) => !sub.requiresAccountEditor
      );
      return {
        ...category,
        subSections,
        route: subSections[0]?.route ?? category.route,
      };
    })
    .filter((category) => category.subSections.length > 0);
};
